import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { 
  Building2, Users, Smartphone, Activity, Shield, AlertTriangle, 
  TrendingUp, Clock, DollarSign, Server, Zap, BarChart3, Search,
  Filter, Download, RefreshCw, Play, Pause, Trash2, ArrowRightLeft
} from 'lucide-react';
import { useEnterpriseAuth } from '../context/EnterpriseAuthContext';
import api from '../utils/api';

const ProductionDashboard = () => {
  const { user, organization, hasPermission } = useEnterpriseAuth();
  const [analytics, setAnalytics] = useState(null);
  const [deviceFleets, setDeviceFleets] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [billingReports, setBillingReports] = useState(null);
  const [selectedProfiles, setSelectedProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [fleetFilter, setFleetFilter] = useState('all');

  const fetchDashboardData = useCallback(async () => {
    try {
      const [analyticsRes, fleetsRes, profilesRes] = await Promise.all([
        api.get('/analytics/dashboard'),
        api.get('/device-fleets'),
        api.get('/profiles?limit=100')
      ]);
      
      setAnalytics(analyticsRes.data);
      setDeviceFleets(fleetsRes.data);
      setProfiles(profilesRes.data);

      if (hasPermission('view_billing')) {
        const billingRes = await api.get('/billing/reports');
        setBillingReports(billingRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [hasPermission]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const executeLifecycleOperation = async (profileId, operation, deviceId = null) => {
    try {
      const payload = deviceId ? { device_id: deviceId } : {};
      await api.post(`/profiles/${profileId}/lifecycle/${operation}`, payload);
      await fetchDashboardData(); // Refresh data
    } catch (error) {
      console.error(`Failed to execute ${operation}:`, error);
    }
  };

  const executeBatchOperation = async (operation) => {
    try {
      await Promise.all(
        selectedProfiles.map(profileId => 
          api.post(`/profiles/${profileId}/lifecycle/${operation}`)
        )
      );
      setSelectedProfiles([]);
      await fetchDashboardData();
    } catch (error) {
      console.error(`Failed to execute batch ${operation}:`, error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800 border-green-200',
      suspended: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      pending: 'bg-blue-100 text-blue-800 border-blue-200',
      revoked: 'bg-red-100 text-red-800 border-red-200',
      deleted: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[status] || colors.pending;
  };

  const getRoleColor = (role) => {
    const colors = {
      operator: 'bg-purple-100 text-purple-800',
      enterprise_admin: 'bg-blue-100 text-blue-800',
      enterprise_provisioner: 'bg-indigo-100 text-indigo-800',
      finance: 'bg-green-100 text-green-800',
      read_only_audit: 'bg-gray-100 text-gray-800'
    };
    return colors[role] || colors.read_only_audit;
  };

  const filteredProfiles = profiles.filter(profile => {
    const matchesSearch = !searchTerm || 
      profile.iccid.toLowerCase().includes(searchTerm.toLowerCase()) ||
      profile.eid.toLowerCase().includes(searchTerm.toLowerCase()) ||
      profile.assigned_user_email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || profile.status === statusFilter;
    const matchesFleet = fleetFilter === 'all' || profile.fleet_id === fleetFilter;
    
    return matchesSearch && matchesStatus && matchesFleet;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading production dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between bg-white p-6 rounded-lg shadow-sm">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">NexoraSIM Production Center</h1>
          <div className="flex items-center gap-4 mt-2">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-600 font-medium">{organization?.name}</span>
            </div>
            <Badge className={getRoleColor(user?.role)}>
              {user?.role?.replace('_', ' ').toUpperCase()}
            </Badge>
            <div className="flex items-center gap-2 text-sm text-green-600 font-medium">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span>Production Live</span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchDashboardData} className="hover:bg-gray-50">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh Data
          </Button>
          {hasPermission('create_profile') && (
            <Button className="bg-blue-600 hover:bg-blue-700 font-medium">
              <Smartphone className="h-4 w-4 mr-2" />
              Create Profile
            </Button>
          )}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <Card className="bg-white shadow-sm border-l-4 border-l-blue-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-semibold">Total Profiles</CardTitle>
            <Smartphone className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">{analytics?.total_profiles || 0}</div>
            <p className="text-xs text-gray-600 font-medium">
              {organization?.max_profiles - (analytics?.total_profiles || 0)} remaining capacity
            </p>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Profiles</CardTitle>
            <Activity className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{analytics?.active_profiles || 0}</div>
            <p className="text-xs text-muted-foreground">
              {((analytics?.active_profiles || 0) / (analytics?.total_profiles || 1) * 100).toFixed(1)}% active
            </p>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Device Fleets</CardTitle>
            <Server className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{analytics?.total_fleets || 0}</div>
            <p className="text-xs text-muted-foreground">Managed fleets</p>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{analytics?.success_rate || 0}%</div>
            <p className="text-xs text-muted-foreground">30-day average</p>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Cost</CardTitle>
            <DollarSign className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">${analytics?.monthly_cost?.toFixed(2) || '0.00'}</div>
            <p className="text-xs text-muted-foreground">Current period</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="profiles" className="space-y-4">
        <TabsList className="bg-white">
          <TabsTrigger value="profiles">Profile Management</TabsTrigger>
          <TabsTrigger value="fleets">Device Fleets</TabsTrigger>
          {hasPermission('view_billing') && (
            <TabsTrigger value="billing">Billing & Finance</TabsTrigger>
          )}
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="profiles" className="space-y-4">
          {/* Filters and Search */}
          <Card className="bg-white shadow-sm">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-center justify-between">
                <div className="flex gap-4 items-center">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search profiles..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 w-64"
                    />
                  </div>
                  
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                      <SelectItem value="revoked">Revoked</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={fleetFilter} onValueChange={setFleetFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Fleet" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Fleets</SelectItem>
                      {deviceFleets.map(fleet => (
                        <SelectItem key={fleet.id} value={fleet.id}>{fleet.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-2">
                  {selectedProfiles.length > 0 && (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => executeBatchOperation('activation')}
                      >
                        <Play className="h-4 w-4 mr-1" />
                        Activate ({selectedProfiles.length})
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => executeBatchOperation('suspension')}
                      >
                        <Pause className="h-4 w-4 mr-1" />
                        Suspend
                      </Button>
                    </>
                  )}
                  <Button variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-1" />
                    Export
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Profiles Table */}
          <Card className="bg-white shadow-sm">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <input 
                        type="checkbox" 
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedProfiles(filteredProfiles.map(p => p.id));
                          } else {
                            setSelectedProfiles([]);
                          }
                        }}
                      />
                    </TableHead>
                    <TableHead>ICCID</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Device Type</TableHead>
                    <TableHead>Assigned User</TableHead>
                    <TableHead>Fleet</TableHead>
                    <TableHead>Cost Center</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProfiles.map((profile) => (
                    <TableRow key={profile.id}>
                      <TableCell>
                        <input 
                          type="checkbox"
                          checked={selectedProfiles.includes(profile.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedProfiles([...selectedProfiles, profile.id]);
                            } else {
                              setSelectedProfiles(selectedProfiles.filter(id => id !== profile.id));
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-sm">{profile.iccid}</TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(profile.status)}>
                          {profile.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{profile.device_type.replace('_', ' ')}</TableCell>
                      <TableCell>{profile.assigned_user_email || '-'}</TableCell>
                      <TableCell>
                        {deviceFleets.find(f => f.id === profile.fleet_id)?.name || '-'}
                      </TableCell>
                      <TableCell>{profile.cost_center || '-'}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {profile.status === 'pending' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => executeLifecycleOperation(profile.id, 'activation')}
                            >
                              <Play className="h-3 w-3" />
                            </Button>
                          )}
                          {profile.status === 'active' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => executeLifecycleOperation(profile.id, 'suspension')}
                            >
                              <Pause className="h-3 w-3" />
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => executeLifecycleOperation(profile.id, 'reassignment')}
                          >
                            <ArrowRightLeft className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="fleets" className="space-y-4">
          <div className="grid gap-4">
            {deviceFleets.map((fleet) => (
              <Card key={fleet.id} className="bg-white shadow-sm">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>{fleet.name}</CardTitle>
                    <Badge variant="outline">{fleet.device_type.replace('_', ' ')}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Total Devices</p>
                      <p className="font-semibold">{fleet.total_devices}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Active Profiles</p>
                      <p className="font-semibold text-green-600">{fleet.active_profiles}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">MDM Provider</p>
                      <p className="font-semibold">{fleet.mdm_provider || 'None'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Cost Center</p>
                      <p className="font-semibold">{fleet.cost_center || 'Default'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {hasPermission('view_billing') && (
          <TabsContent value="billing" className="space-y-4">
            <Card className="bg-white shadow-sm">
              <CardHeader>
                <CardTitle>Billing Summary</CardTitle>
              </CardHeader>
              <CardContent>
                {billingReports && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      {Object.entries(billingReports.cost_center_summary).map(([center, data]) => (
                        <div key={center} className="p-4 bg-gray-50 rounded-lg">
                          <h4 className="font-semibold">{center}</h4>
                          <p className="text-sm text-gray-600">{data.profiles} profiles</p>
                          <p className="text-lg font-bold">${data.total_cost.toFixed(2)}</p>
                          <p className="text-xs text-gray-500">{data.usage_mb.toFixed(1)} MB</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default ProductionDashboard;